import { typeGuard_ContentNodeStatus } from "../types/Content";
import { folderWeightMaxValue, mariaDbSignedIntMaxValue, nodeWeightMaxValue, pdfFileNofPagesMaxValue, pdfFileSizeMaxValue, podAssetSizeMaxValue, pdfPageDimMaxValue, pdfPageEyeSecondsMaxValue, pdfPageMBottomMaxValue, pdfPageMLeftMaxValue, pdfPageMRightMaxValue, pdfPageMTopMaxValue, pdfPageNoMaxValue, pdfPageRotationMaxValue, reactionCountMaxValue, viewSubMaxValue } from "./validationConstantsNumeric";
import { typeGuard_InteractionType, typeGuard_iLinkLinkType, typeGuard_iLinkWhich, typeGuard_InteractionTypePlural, Rect } from "../types/Interaction";
import { typeGuard_ReactableType, typeGuard_ViewableType, typeGuard_downloadableType } from "../types/Miscs";
import { typeGuard_OpCode } from "../types/OpCodes";
import { syncPullRequest, typeGuard_PodMemberShipStatus } from "../types/Pod";
import { ValidationError } from "./validationError";
import { expressSessionIdMaxLength, notExpressSessionIdRegex, appVersionMaxLength, pwaErrorReportMaxLength, uuidMaxLength, notUuidRegex, usernameMaxLength, oidcIssMaxLength, oidcSubMaxLength, satosaNameIdMaxLength, satosaEntityIdMaxLength, podNameMaxLength, podKeyphraseMaxLength, tagNameMaxLength, tagDescriptionMaxLength, folderNameMaxLength, folderDescriptionMaxLength, threadNameMaxLength, anchorRelTextMaxLength, interactionLabelMaxLength, msgTextMaxLength, pdfFileNameMaxLength, pdfFileDescriptionMaxLength, pdfFileHashMaxLength, pdfPageFulltextMaxLength, fingerprintDigestMaxLength, defaultMaxLength, notUserNameRegex, colorCodeRegex, colorCodeRegexShort, notAsciiRegex, podDescriptionMaxLength, notInstantLoginRegex, instantLoginMaxLength, instantUserPwMinLength, podIdHrMaxLength, notPodIdHrRegex, podAssetTypes, notMimeTypeRegex, podKeyphraseMinLength, instantUserPwMaxLength, opDataMaxLength, urlRegex } from "./validationConstantsString";
import { MinimalLogger } from "../types/minimalLogger";
import { typeGuard_Permission } from "../types/Permissions";

// ------------- Base Functions for Validation

/**
 *
 * @param {string} input
 * @param {string} target
 * @param {MinimalLogger} logger
 * @returns {void}
 */
export const validateString = function(input:string, target:string, logger: MinimalLogger):void {
    try {

        if (typeof input === 'undefined') throw new ValidationError(`The input value to validate as ${target} is undefined and thus cannot be evaluated`)

        switch (target) {

          case 'expressSessionId':
            if (input.length > expressSessionIdMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            if (String(input).match(notExpressSessionIdRegex)) throw new ValidationError(`Uuid \'${input}\' contains invalid characters!`)
            return
          case 'appVersion':
            if (input.length > appVersionMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'pwaErrorReport':
            if (input.length > pwaErrorReportMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'uuid': // podId, opLogId, ...
            if (input.length > uuidMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            if (String(input).match(notUuidRegex)) throw new ValidationError(`Uuid \'${input}\' contains invalid characters!`)
            return

          case 'usergroupId':
            if (input.length > uuidMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            if (input !== '*private') {
              if (String(input).match(notUuidRegex)) throw new ValidationError(`Uuid \'${input}\' contains invalid characters!`)
            }
            return

          case 'username':
            if (input.length > usernameMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            // No line breaks, nothing from UTF character classes Control (C), Separator (Z) and Punctuation (P), except space, comma, hyphen, period and underscore

            if (String(input).match(notUserNameRegex)) throw new ValidationError(`Username \'${input}\' contains invalid characters!`)
            return

          case 'instantLogin':
            if (input.length > instantLoginMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            // No line breaks, nothing from UTF character classes Control (C), Separator (Z) and Punctuation (P), except space, comma, hyphen, period and underscore
            if (String(input).match(notInstantLoginRegex)) throw new ValidationError(`Username \'${input}\' contains invalid characters !`)
            return

          case 'instantUserPw':
            const pwInputLength = input.length
            if (pwInputLength < instantUserPwMinLength) throw new ValidationError(`Length below required min length (${instantUserPwMinLength}) for target \'${target}\'!  Input Length was: ${pwInputLength}`);
            if (pwInputLength > instantUserPwMaxLength) throw new ValidationError(`Length exceeds allowed max length (${instantUserPwMaxLength})for target \'${target}\'!  Input Length was: ${pwInputLength}`);
            return

          case 'oidc.iss':
            if (input.length > oidcIssMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'oidc.sub':
            if (input.length > oidcSubMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'satosa.nameId':
            if (input.length > satosaNameIdMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'satosa.eduPersonScopedAffiliation':
            // assuming the right part of DFN's eduPersonScopedAffiliation (https://doku.tid.dfn.de/de:common_attributes#a09) is SAML Entity ID (8.3.6 Entity Identifier http://docs.oasis-open.org/security/saml/v2.0/saml-core-2.0-os.pdf)
            // eduPersonScopedAffiliation could be validated against fixed values (faculty, student, staff, alum, member, affiliate, employee, library-walk-in)
            if (input.length > satosaEntityIdMaxLength + 20) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'! Value was: ${input}`);
            return


          case 'pod.podIdHr':
            if (input.length > podIdHrMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            if (String(input).match(notPodIdHrRegex)) throw new ValidationError(`Human-readable podId \'${input}\' contains invalid characters!`)
            return
          case 'pod.name':
            if (input.length > podNameMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'pod.keyphrase':
            const keyphraseInputLength = input.length
            if (keyphraseInputLength > podKeyphraseMaxLength) throw new ValidationError(`Length exceeds allowed max value (${podKeyphraseMaxLength}) for target \'${target}\'!  Input Length was: ${keyphraseInputLength}`);
            if (keyphraseInputLength < podKeyphraseMinLength) throw new ValidationError(`Length below required min value (${podKeyphraseMinLength}) for target \'${target}\'!  Input Length was: ${keyphraseInputLength}`);
            return
          case 'pod.description':
            if (input.length > podDescriptionMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return

          case 'tag.name':
            if (input.length > tagNameMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            //if (String(input).match(notTagNameRegex)) throw new ValidationError(`Tagname \'${input}\' contains invalid characters!`)

            return
          case 'tag.description':
            if (input.length > tagDescriptionMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return

          case 'folder.name':
            if (input.length > folderNameMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'folder.description':
            if (input.length > folderDescriptionMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return

          case 'thread.name':
            if (input.length > threadNameMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'anchor.relText':
            if (input.length > anchorRelTextMaxLength) throw new ValidationError(`Length ${input.length} exceeds allowed max value ${anchorRelTextMaxLength} for target \'${target}\'!`);
            return
          case 'interaction.label':
            if (input.length > interactionLabelMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'msg.text':
            if (input.length > msgTextMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return

          case 'pdfFile.name':
            if (input.length > pdfFileNameMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            //if (String(input).match(notPdfFileNameRegex)) throw new ValidationError(`PdfFileName \'${input}\' contains invalid characters!`)
            return
          case 'pdfFile.description':
            if (input.length > pdfFileDescriptionMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return
          case 'pdfFile.hash':
            if (input.length > pdfFileHashMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return

          case 'pdfPage.fullText':
            if (input.length > pdfPageFulltextMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'${target}\'!  Value was: ${input}`);
            return

          case 'file.type':
            if (podAssetTypes.indexOf(input)<0) throw new ValidationError(`${input} is not in podAssetTypes[${podAssetTypes.join(', ')}]`)
            return

          case 'fingerprintDigest':
            if (input.length > fingerprintDigestMaxLength) throw new ValidationError(`Length ${input.length} exceeds allowed max value ${fingerprintDigestMaxLength} for target \'${target}\'!`);
            return

          case 'mimeType':
            if (input.length > 36) throw new ValidationError(`Length ${input.length} exceeds allowed max value of 36`)
            if (input.match(notMimeTypeRegex)) throw new ValidationError(`${input} contains invalid characters for mimeType`)
            return

          case 'opData':
            if (input.length > opDataMaxLength) {
              const data = JSON.parse(input);
              throw new ValidationError(`Length ${input.length} exceeds allowed max value of ${opDataMaxLength}. Rects: ${data.anchor.rects.length} Objects, relText: ${data.anchor.relText.length} chars, label: ${data.label.length} chars`)
            }
            return

          default:
            logger?.warn(`Unknown validation target for string length: ${target}!  Value was: ${input}`)
            if (input.length > defaultMaxLength) throw new ValidationError(`Length exceeds allowed max value for target \'default\'!  Value was: ${input}`);
            return
        }


    } catch (error) {
        throw error;
    }
}

/**
 *
 * @param {number} input
 * @param {string} target
 * @param {MinimalLogger} logger
 * @returns {void}
 */
export const validateNumber = function (input:number, target:string, logger:MinimalLogger): void {
  try {
    if (typeof(input) === 'string') logger?.warn(`Input ${input} has type string, expected number, casting! Target was: '${target}'`)

    let parsedInput:number;

    // general numeric checks
    if (target !== 'interactionRect') {
      parsedInput = (typeof(input) === 'string') ? parseInt(input) : input
      if (!Number.isInteger(parsedInput)) throw new ValidationError(`Input ${parsedInput} (type: ${typeof(parsedInput)}) is not an integer! target was: '${target}'`) // includes checking for null/undefined -> false

    } else { // float
      parsedInput = (typeof(input) === 'string') ? parseFloat(input) : input
      if (Number.isNaN(parsedInput)) throw new ValidationError(`Input ${parsedInput} (type: ${typeof(parsedInput)}) is not an number! target was: '${target}'`)
    }

    const rangeErrorMsg = `Integer value out of Range for target \'${target}\'!`;



    switch (target) {
      case 'id': // uid, coid,
        if (!(parsedInput >= 0 && parsedInput <= mariaDbSignedIntMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'folder.weight':
        if (!(parsedInput >= 0 && parsedInput <= folderWeightMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfFile.weight':
        if (!(parsedInput >= 0 && parsedInput <= nodeWeightMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'timestamp':
        if (!(parsedInput >= 0 && parsedInput <= mariaDbSignedIntMaxValue)) throw new ValidationError(`Integer value ${parsedInput} for timestamp is out of range 0 < t < ${mariaDbSignedIntMaxValue} for target \'${target}\'!`);
        return
      case 'timestamp.due':
        const min = Math.floor(Date.now() / 1000);
        if (!(parsedInput >= min && parsedInput <= mariaDbSignedIntMaxValue)) throw new ValidationError(`Integer value ${parsedInput} out of range [${min}:${mariaDbSignedIntMaxValue}] for target \'${target}\'!`);
        return
      case 'reactionCount':
        if (!(parsedInput >= 0 && parsedInput <= reactionCountMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'view.sub':
        if (!(parsedInput >= 0 && parsedInput <= viewSubMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'msgThread.views':
        if (!(parsedInput >= 0 && parsedInput <= mariaDbSignedIntMaxValue)) throw new ValidationError(rangeErrorMsg);
        return

      case 'file.size':
      case 'pdfFile.size':
        if (!(parsedInput >= 0 && parsedInput <= pdfFileSizeMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfFile.nofPages':
        if (!(parsedInput >= 0 && parsedInput <= pdfFileNofPagesMaxValue)) throw new ValidationError(rangeErrorMsg);
        return

      case 'podAsset.size':
        if (!(parsedInput >= 0 && parsedInput <= podAssetSizeMaxValue)) throw new ValidationError(rangeErrorMsg);
        return

      case 'pdfPage.no':
        if (!(parsedInput >= 0 && parsedInput <= pdfPageNoMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.dimension':
        if (!(parsedInput >= 0 && parsedInput <= pdfPageDimMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'interactionRect':
        // allowing values < 0 for now until we know where they come from
        if (!(parsedInput <= pdfPageDimMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.rotation': //tinyint
        if (!(parsedInput >= 0 && parsedInput <= pdfPageRotationMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.mTop':
        if (!(parsedInput >= 0 && parsedInput <= pdfPageMTopMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.mRight':
        if (!(parsedInput >= 0 && parsedInput <= pdfPageMRightMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.mBottom':
        if (!(parsedInput >= 0 && parsedInput <= pdfPageMBottomMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.mLeft':
        if (!(parsedInput >= 0 && parsedInput <= pdfPageMLeftMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.eyeSeconds':
        if (!(parsedInput >= 0 && parsedInput <= pdfPageEyeSecondsMaxValue)) throw new ValidationError(rangeErrorMsg);
        return
      case 'pdfPage.dSeen':
        //if (!(parsedInput >= 0 && parsedInput <= pdfPageDSeenMaxValue)) throw new ValidationError(rangeErrorMsg);
        return

      default:
        logger?.warn(`Unknown validation target for integer size: ${target}!`)
        if (parsedInput > mariaDbSignedIntMaxValue) throw new ValidationError('Integer value out of Range for target \'default\'!'); // other value?
        return

    }
  } catch (error) {
    throw error;
  }
}


// ------------- Misc Checks

/**
 *
 * @param {Rect} input
 * @param {MinimalLogger} logger
 * @returns {void}
 */
export const validateInteractionRect = function (input:Rect, logger:MinimalLogger): void {
  try {
      validateNumber(input.x, 'interactionRect', logger);
      validateNumber(input.y, 'interactionRect', logger);
      validateNumber(input.w, 'interactionRect', logger);
      validateNumber(input.h, 'interactionRect', logger);
      validateNumber(input.p, 'pdfFile.nofPages', logger);
    return;

  } catch (error) {
    throw error;
  }
}

/**
 *
 * @param {Rect[]} input
 * @param {MinimalLogger} logger
 * @returns {void}
 */
export const validateInteractionRects = function (input:Rect[], logger:MinimalLogger): void {
  try {
    for (const rect of input) {
      validateInteractionRect(rect, logger);
    };

    return;

  } catch (error) {
    throw error;
  }
}

/**
 *
 * @param {string} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateAsciiOnly = function (input:string): void {

  try {
    if (notAsciiRegex.test(input)) throw new ValidationError('String contains non-ASCII characters!');
    return
  } catch (error) {
    throw error;
  }
}

/**
 * If applicable (null value allowed), validate Color Code against Hex-Code Regex with leading '#'
 * @param {string} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateColorCode = function (input:string): void {

  try {
    if (!(
      (input.length === 7 && colorCodeRegex.test(input))
      ||
      (input.length === 4 && colorCodeRegexShort.test(input))
      )
    ) throw new ValidationError('Color Code does not match pattern!');
    return
  } catch (error) {
    throw error;
  }
}

/**
 * Validation function for Urls, not overly strict, focussing on http/https.
 * Already used in frontend and backend. Not overly complex, so no logger needed (for now).
 * @param {string} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateURL = function (input:string): void {
  try {

    if(input.match(urlRegex)) {
      return
    } else {
      throw Error(`Input did not match URL-Regex. Input was: '${input}'`)
    }

  } catch (error) {
    throw new ValidationError(`Input URL is not a valid URL: ${error}`)
  }
}

/**
 * Checks if input value is present in list of known emojis.
 * Emoji-List is retrieved from static file on app and loaded separetely in backend.
 * Backend-Only Function?
 * @param {string[]} staticEmojis
 * @param {string} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateUtf8EmojiStatic = function (staticEmojis:string[], input:string): void {
  try {
    if (staticEmojis.includes(input)) return;
    throw new ValidationError(`Input String ${input} is not a valid (STATIC) UTF-8 Emoji`);
  } catch (error) {
    throw error;
  }
}

/**
 * Validates both real boolean values as well as strings 'true'/'false' atm.
 * Might be pragmatic backend-only logic for inconsistent boolean values from client?
 * If string values cannot be avoided, this function should be split.
 * @param {boolean | string} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateBoolean = function (input:boolean | string): void {
  try {
    if (typeof input === 'string') {
      if (!(input === 'true' || input === 'false')) throw new ValidationError('String input does not represent a boolean value!')
      return
    }
    if (typeof input !== 'boolean') throw new ValidationError('Input is not a boolean value!')
    return
  } catch (error) {
    throw error;
  }
}

/**
 *
 * @param {string} validationFn
 * @param {string} input
 * @returns {void}
 * @throws {ValidationError}
 *
 */
// export const validateIpAddress = function (input:string): void {
//   try {
//     if (!ipaddr.isValid(input)) throw new ValidationError('String input is not a valid ip address!');
//   } catch (error) {
//     throw error;
//   }
// }

export const validateIpAddress = function (validationFn:Function,input:string): void {
  try {
    if (!validationFn(input)) throw new ValidationError('String input is not a valid ip address!');
  } catch (error) {
    throw error;
  }
}

/**
 *
 * @param {syncPullRequest} input
 * @param {MinimalLogger} logger
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateSyncPullRequest = function (input:syncPullRequest, logger:MinimalLogger): void {
  try {
      validateString(input.podId, 'uuid', logger);
      validateNumber(input.lastSyncOid, 'id', logger);
      validateNumber(input.tRequested, 'timestamp', logger);
      return;
  } catch (error) {
    throw error;
  }
}

/**
 *
 * @param {{login:string, userName:string, password:string}} input
 * @param {MinimalLogger} logger
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateInstantUserData = function (input:{login:string, userName:string, password:string}, logger:MinimalLogger):void {
  try {
      validateString(input.login, 'instantLogin', logger)
      validateString(input.userName, 'username', logger)
      validateString(input.password, 'instantUserPw', logger)
      return;

  } catch (error) {
    throw error;
  }
}

//------------- Type Checks at Runtime

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateInteractionType = function(input:any):void {
  try {
    if (!typeGuard_InteractionType(input)) throw new ValidationError('Invalid InteractionType!')
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateInteractionTypePlural = function(input:any):void {
  try {
    if (!typeGuard_InteractionTypePlural(input)) throw new ValidationError('Invalid InteractionTypePlural! ' + input)
  } catch (error) {
    throw error;
  }
};


/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateOpCode = function(input:any):void {
  try {
    if (!typeGuard_OpCode(input)) throw new ValidationError('Invalid OpCode!')
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validatePermission = function(input:any):void {
  try {
    if (!typeGuard_Permission(input)) throw new ValidationError('Invalid Permission!')
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateiLinkType = function(input:any):void {
  try {
    if (!typeGuard_iLinkLinkType(input)) throw new ValidationError('Invalid iLinkType!')
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateiLinkWhich = function(input:any):void {
  try {
    if (!typeGuard_iLinkWhich(input)) throw new ValidationError('Invalid iLinkWhich!')
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateViewableType = function(input:any):void {
  try {
    if (!typeGuard_ViewableType(input)) throw new ValidationError('Invalid ViewableType!')
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateReactableType = function(input:any):void {
  try {
    if (!typeGuard_ReactableType(input)) throw new ValidationError(`Invalid ReactableType ${input}!`)
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateDownloadableType = function(input:any):void {
  try {
    if (!typeGuard_downloadableType(input)) throw new ValidationError(`Invalid DownloadableType ${input}!`)
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validateContentNodeStatus = function(input:any):void {
  try {
    if (!typeGuard_ContentNodeStatus(input)) throw new ValidationError('Invalid ContentNodeStatus!')
  } catch (error) {
    throw error;
  }
};

/**
 *
 * @param {any} input
 * @returns {void}
 * @throws {ValidationError}
 */
export const validatePodMembershipStatus = function(input:any):void {
  try {
    if (!typeGuard_PodMemberShipStatus(input)) throw new ValidationError('Invalid iLinkWhich!')
  } catch (error) {
    throw error;
  }
};;